import * as React from "react";
import { DatePicker } from "react-ui";

import { isValidStringDate, parseDateStringAsDate } from "../../../../Helpers/DateHelpers";
import { FilterExpression, OperatorType } from "../../../../Helpers/FilterExpressions/FilterExpression";
import { FILTER_DATE_TYPE } from "../../../metricsAction";
import { MetricsActionPayload, trackAction } from "../../../Internals/Metrics/MetricsProvider";
import { pullContext } from "../../../TypingUtils";
import { FilterSourceInfoType, MultiLineFilter } from "../../MultiLine/MultiLineFilter";
import { AbsoluteModelPath } from "../../../../../../Common/ModelPath/ModelPath";
import { tablePathContextTypes } from "../../CrossfitTable/TablePathContext";
import { DateTimeNetUtils } from "../../../../../../Common/DateTimeNetUtils";

export interface FilterDateRangeProps {
    dataPath: AbsoluteModelPath;

    endDate: string;
    startDate: string;

    convertDateToTicksInFilterExpression?: boolean;

    onChangeFilter: (stickyTablePath: string, filter: MultiLineFilter) => void;
    onRemoveFilter: (stickyTablePath: string, filterSourceId: string) => void;
}

interface FilterDateRangeState {
    endDate: string;
    startDate: string;
}

interface FilterDateRangeContext {
    stickyTablePath: string;
}

function filterDateRangeSelector(
    props: FilterDateRangeProps,
    _: FilterDateRangeState,
    type: string,
    value: string
): MetricsActionPayload {
    return {
        path: props.dataPath,
        type: type,
        value: value,
    };
}

export class FilterDateRange extends React.Component<FilterDateRangeProps, FilterDateRangeState> {
    public static contextTypes = tablePathContextTypes;

    public constructor(props: FilterDateRangeProps) {
        super(props);
        this.state = {
            startDate: this.props.startDate,
            endDate: this.props.endDate,
        };
    }

    public render(): JSX.Element {
        return (
            <div>
                <DatePicker
                    value={this.state.startDate}
                    data-tid="FilterStartDate"
                    onValueChange={this.handleOnChangeStartDate}
                />
                <span>{" — "}</span>
                <DatePicker
                    value={this.state.endDate}
                    data-tid="FilterEndDate"
                    onValueChange={this.handleOnChangeEndDate}
                />
            </div>
        );
    }

    private readonly handleOnChangeStartDate = (value: string): void => {
        this.setState({ startDate: value });
        this.handleOnChangeDate(OperatorType.StartDate, value);
    };

    private readonly handleOnChangeEndDate = (value: string): void => {
        this.setState({ endDate: value });
        this.handleOnChangeDate(OperatorType.EndDate, value);
    };

    private handleOnChangeDate(type: OperatorType, value: string): void {
        if (isValidStringDate(value) || value === "") {
            setTimeout(() => this.runFilter(type, value), 500);
        }
    }

    private makeFilterExpressionForDecimalRepresentation(
        valueType: OperatorType.LessOrEqual | OperatorType.GreaterOrEqual,
        value: string
    ) {
        const parsedValue = parseDateStringAsDate(value);
        const valueInNetTicks = parsedValue != undefined ? DateTimeNetUtils.toNetTicks(parsedValue) : undefined;
        return valueInNetTicks != undefined
            ? {
                  operator: valueType,
                  condition: {
                      dataPath: this.props.dataPath.toLegacyPath(),
                      value: valueInNetTicks.toString(),
                  },
              }
            : undefined;
    }

    private makeFilterExpressionForDateRepresentation(
        valueType: OperatorType.StartDate | OperatorType.EndDate,
        value: string
    ) {
        return value != ""
            ? {
                  operator: valueType,
                  condition: {
                      dataPath: this.props.dataPath.toLegacyPath(),
                      value: value,
                  },
              }
            : undefined;
    }

    @trackAction(FILTER_DATE_TYPE, filterDateRangeSelector)
    private runFilter(valueType: OperatorType, value: string): void {
        const makeDecimalConditions = this.props.convertDateToTicksInFilterExpression;
        const startDateValue = valueType === OperatorType.StartDate ? value : this.state.startDate;
        const endDateValue = valueType === OperatorType.EndDate ? value : this.state.endDate;

        const startCondition: FilterExpression | undefined = makeDecimalConditions
            ? this.makeFilterExpressionForDecimalRepresentation(OperatorType.GreaterOrEqual, startDateValue)
            : this.makeFilterExpressionForDateRepresentation(OperatorType.StartDate, startDateValue);

        const endCondition: FilterExpression | undefined = makeDecimalConditions
            ? this.makeFilterExpressionForDecimalRepresentation(OperatorType.LessOrEqual, endDateValue)
            : this.makeFilterExpressionForDateRepresentation(OperatorType.EndDate, endDateValue);

        const filterValue: FilterExpression | undefined =
            startCondition != undefined && endCondition != undefined
                ? {
                      operator: OperatorType.And,
                      conditions: [startCondition, endCondition],
                  }
                : startCondition || endCondition;

        const filter: undefined | MultiLineFilter =
            filterValue != undefined
                ? {
                      filterSourceId: this.props.dataPath.toLegacyPath(),
                      filterSourceInfo: {
                          type: FilterSourceInfoType.DateRange,
                          startDate: startDateValue,
                          endDate: endDateValue,
                      },
                      expression: filterValue,
                  }
                : undefined;
        if (filter != undefined) {
            this.props.onChangeFilter(this.getContext().stickyTablePath, filter);
        } else {
            this.props.onRemoveFilter(this.getContext().stickyTablePath, this.props.dataPath.toLegacyPath());
        }
    }

    private getContext(): FilterDateRangeContext {
        return pullContext<FilterDateRangeContext>(this);
    }
}
